import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface UiState {
  isLoading: boolean;
  isLoadingSmall: boolean;
  loadingText?: string;
  activeRequests: number; // For tracking concurrent requests
  isWalletOpen: boolean;
  isRewardsOpen: boolean;
  rewardsActiveTab: string;
  isLoginOpen: boolean;
  isRegisterOpen: boolean;
  isBetDetailsOpen: boolean;
  betDetailsRoundId: string | null;
  betDetailsInitialData: any | null;
}

const initialState: UiState = {
  isLoading: false,
  isLoadingSmall: false,
  loadingText: 'Loading...',
  activeRequests: 0,
  isWalletOpen: false,
  isRewardsOpen: false,
  rewardsActiveTab: 'bonuses',
  isLoginOpen: false,
  isRegisterOpen: false,
  isBetDetailsOpen: false,
  betDetailsRoundId: null,
  betDetailsInitialData: null,
};

const BACKGROUND_ACTIONS = [
  'spinLevel',
  'fetchDepositAddress',
  'fetchLevels',
  'fetchSiteConfig',
  'createWithdrawal',
  'transferBalance',
  'fetchPayoutSummary',
  'fetchCampaigns',
  'fetchReferrals',
  'bonusesApi',
  'activateBonus',
  'cancelBonus',
  'forfeitBonus',
  'redeemPromoCode',
  'createCampaign',
  'auth/login',
  'auth/googleLogin',
  'auth/register',
  'fetchMyLevel',
  'fetchUserBalance',
  'gamesApi',
  'paymentApi',
];

const isBackgroundAction = (actionType: string) => {
  return BACKGROUND_ACTIONS.some((action) => actionType.includes(action));
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    showLoader: (
      state,
      action: PayloadAction<{ text?: string; isLoaderSmall?: boolean } | undefined>,
    ) => {
      if (action.payload?.isLoaderSmall) {
        state.isLoadingSmall = true;
      } else {
        state.isLoading = true;
      }
      state.loadingText = action.payload?.text || 'Loading...';
      state.activeRequests++;
    },
    hideLoader: (state) => {
      state.activeRequests--;
      if (state.activeRequests <= 0) {
        state.activeRequests = 0;
        state.isLoading = false;
        state.isLoadingSmall = false;
        state.loadingText = 'Loading...';
      }
    },
    showLoaderSmall: (state) => {
      state.isLoadingSmall = true;
      state.activeRequests++;
    },
    hideLoaderSmall: (state) => {
      state.activeRequests--;
      if (state.activeRequests <= 0) {
        state.activeRequests = 0;
        state.isLoadingSmall = false;
      }
    },
    setWalletOpen: (state, action: PayloadAction<boolean>) => {
      state.isWalletOpen = action.payload;
    },
    setRewardsOpen: (state, action: PayloadAction<boolean>) => {
      state.isRewardsOpen = action.payload;
    },
    setRewardsTab: (state, action: PayloadAction<string>) => {
      state.rewardsActiveTab = action.payload;
    },
    setLoginOpen: (state, action: PayloadAction<boolean>) => {
      state.isLoginOpen = action.payload;
    },
    setRegisterOpen: (state, action: PayloadAction<boolean>) => {
      state.isRegisterOpen = action.payload;
    },
    openBetDetailsModal: (
      state,
      action: PayloadAction<{ roundId?: string | number | null; initialData?: any } | undefined>,
    ) => {
      state.isBetDetailsOpen = true;
      state.betDetailsRoundId = action?.payload?.roundId ? String(action.payload.roundId) : null;
      state.betDetailsInitialData = action?.payload?.initialData || null;
    },
    closeBetDetailsModal: (state) => {
      state.isBetDetailsOpen = false;
      state.betDetailsRoundId = null;
      state.betDetailsInitialData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) => action.type.endsWith('/pending') && !isBackgroundAction(action.type),
        (state, action) => {
          state.activeRequests++;
          // state.isLoading = true;

          if (action.type.includes('login')) {
            state.loadingText = 'Logging in...';
          } else if (action.type.includes('register')) {
            state.loadingText = 'Registering...';
          } else {
            state.loadingText = 'Loading...';
          }
        },
      )
      .addMatcher(
        (action) =>
          (action.type.endsWith('/fulfilled') || action.type.endsWith('/rejected')) &&
          !isBackgroundAction(action.type),
        (state) => {
          state.activeRequests--;
          if (state.activeRequests <= 0) {
            state.activeRequests = 0;
            state.isLoading = false;
            state.loadingText = 'Loading...';
          }
        },
      );
  },
});

export const {
  showLoader,
  hideLoader,
  showLoaderSmall,
  hideLoaderSmall,
  setWalletOpen,
  setRewardsOpen,
  setRewardsTab,
  setLoginOpen,
  setRegisterOpen,
  openBetDetailsModal,
  closeBetDetailsModal,
} = uiSlice.actions;

export default uiSlice.reducer;
